import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import StreamerDetail, { MobileOverlayBanner } from './StreamerDetail';
import RuleEditor from './RuleEditor';
import LivesInteractifs from './LivesInteractifs';
import NSLiveChat from './NSLiveChat';
import LivesMatchs from './LivesMatchs';
import MesMatchs from './MesMatchs';
import EvenementsLive from './EvenementsLive';
import MesEvenements from './MesEvenements';
import Settings from './Settings';
import Ressources from './Ressources';
import Profile from './Profile';
import InstallPrompt from './InstallPrompt';
import MobileOverlay from './MobileOverlay';
import Dashboard, { LeaderTab, AgenceTab } from './Dashboard';
import MixingTableFullscreen from './MixingTableFullscreen';
import AgentChat from './AgentChat';
import ApplyInbox from './ApplyInbox';
import { Streamer, getStreamers, getProfile, getPublicAgency, getMyAccess, AccessInfo } from './api';

const overlayMatch = window.location.pathname.match(/^\/overlay\/(\d+)$/);

type View = 'dashboard' | 'mon-live' | 'ma-team' | 'mon-profil' | 'lives' | 'lives-matchs' | 'mes-matchs' | 'evenements-live' | 'mes-evenements' | 'ressources' | 'chat' | 'settings';
const VIEW_STORAGE_KEY = 'nslive:last-view';
const VALID_VIEWS: View[] = ['dashboard', 'mon-live', 'ma-team', 'mon-profil', 'lives', 'lives-matchs', 'mes-matchs', 'evenements-live', 'mes-evenements', 'ressources', 'chat', 'settings'];

function getStoredView(): View {
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return VALID_VIEWS.includes(stored as View) ? stored as View : 'dashboard';
}

export function useIsMobile() {
  const [v, setV] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

function AppContent() {
  const { user, isLoading, logout } = useAuth();
  const [selected, setSelected] = useState<Streamer | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState<View>(getStoredView);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [sidebarAvatarError, setSidebarAvatarError] = useState(false);
  const [agency, setAgency] = useState<{ name: string | null; logo_url: string | null }>({ name: null, logo_url: null });
  const [agencyLogoError, setAgencyLogoError] = useState(false);
  const [myStreamer, setMyStreamer] = useState<Streamer | null>(null);
  const [leaderGroupe, setLeaderGroupe] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [showMixingTable, setShowMixingTable] = useState(false);
  // Desktop: true=expanded(230px), false=collapsed(56px)
  // Mobile: true=drawer open, false=drawer closed
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isMobile = useIsMobile();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // On mobile default to closed, on desktop default to open
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);

  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  const handleStreamerUpdated = useCallback(() => setRefreshKey(k => k + 1), []);

  // Load profile avatar
  useEffect(() => {
    if (user) {
      getProfile().then(r => setAvatarUrl(r.data.avatar_url)).catch(() => {});
    } else {
      setAvatarUrl(null);
    }
  }, [user]);

  // Load agency info
  useEffect(() => {
    getPublicAgency().then(r => setAgency(r.data)).catch(() => {});
  }, []);

  // Droits effectifs (responsable / modules)
  useEffect(() => {
    if (!user) return;
    getMyAccess().then(r => setAccess(r.data)).catch(() => setAccess(null));
  }, [user]);

  // Load logged-in user's streamer record + leaderGroupe
  useEffect(() => {
    if (!user) return;
    getStreamers().then(r => {
      const sid = user.streamer_id;
      const me = sid != null
        ? (r.data.find(s => s.id === sid) ?? r.data[0] ?? null)
        : (r.data[0] ?? null);
      setMyStreamer(me);
      setLeaderGroupe(me?.leader_groupe ?? null);
      setSidebarAvatarError(false); // reset error when streamer changes
    }).catch(() => {});
  }, [user]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Chargement…</div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const isAdmin = user.role === 'admin';
  const hasStreamer = !!user.streamer_id || !!myStreamer;

  const navigateTo = (v: View) => {
    setView(v);
    setSelected(null);
    if (isMobile) setSidebarOpen(false);
    setUserMenuOpen(false);
  };
  const sideW = sidebarOpen ? 230 : 56;

  const LogoMark = () => (
    <div style={{ width: 34, height: 34, borderRadius: 9, background: agency.logo_url && !agencyLogoError ? '#f8fafc' : 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', border: agency.logo_url && !agencyLogoError ? '1px solid #e2e8f0' : 'none' }}>
      {agency.logo_url && !agencyLogoError
        ? <img src={agency.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} onError={() => setAgencyLogoError(true)} />
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </div>
  );

  const HamburgerBtn = ({ onClick }: { onClick: () => void }) => (
    <button onClick={onClick} style={{ width: isMobile ? 44 : 34, height: isMobile ? 44 : 34, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 10, flexShrink: 0, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
      title="Menu">
      <span style={{ display: 'block', width: 18, height: 2, background: '#64748b', borderRadius: 2 }} />
      <span style={{ display: 'block', width: 18, height: 2, background: '#64748b', borderRadius: 2 }} />
      <span style={{ display: 'block', width: 18, height: 2, background: '#64748b', borderRadius: 2 }} />
    </button>
  );

  const SidebarContent = () => (
    <div style={{ width: isMobile ? 260 : sideW, background: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', transition: isMobile ? 'none' : 'width 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>

      {/* Header: hamburger + logo */}
      <div style={{ padding: '12px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, minHeight: 58 }}>
        <HamburgerBtn onClick={() => setSidebarOpen(o => !o)} />
        {(sidebarOpen || isMobile) && (
          <>
            <LogoMark />
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 14, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agency.name || 'NS Live'}</div>
              <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 500 }}>Animator</div>
            </div>
          </>
        )}
        {!sidebarOpen && !isMobile && <LogoMark />}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Tableau de bord — toujours visible */}
        <SideNavItem
          icon={<IconDashboard />}
          active={!selected && view === 'dashboard'}
          onClick={() => navigateTo('dashboard')}
          collapsed={!sidebarOpen && !isMobile}
          label="Tableau de bord"
        />
        {/* Mes Matchs — streamer ou admin */}
        {(isAdmin || hasStreamer) && (
          <SideNavItem
            icon={<IconMatchs />}
            active={!selected && view === 'mes-matchs'}
            onClick={() => navigateTo('mes-matchs')}
            collapsed={!sidebarOpen && !isMobile}
            label="Mes Matchs"
          />
        )}
        {/* Mes Événements — streamer ou admin */}
        {(isAdmin || hasStreamer) && (
          <SideNavItem
            icon={<IconEvenements />}
            active={!selected && view === 'mes-evenements'}
            onClick={() => navigateTo('mes-evenements')}
            collapsed={!sidebarOpen && !isMobile}
            label="Mes Événements"
          />
        )}
        {/* Mes Lives Interactifs — si streamer associé */}
        {hasStreamer && (
          <SideNavItem
            icon={<IconLiveInteractif />}
            active={!selected && view === 'mon-live'}
            onClick={() => navigateTo('mon-live')}
            collapsed={!sidebarOpen && !isMobile}
            label="Mes Lives Interactifs"
          />
        )}
        {/* Mes ressources — visible par tous les connectés */}
        <SideNavItem
          icon={<IconRessources />}
          active={!selected && view === 'ressources'}
          onClick={() => navigateTo('ressources')}
          collapsed={!sidebarOpen && !isMobile}
          label="Mes ressources"
        />
        {/* Séparateur — modules gestion */}
        {(isAdmin || !!leaderGroupe || access?.can_manage_matchs || access?.can_manage_events || access?.can_manage_lives) && (
          <div style={{ margin: '6px 8px', borderTop: '1px solid #f1f5f9' }} />
        )}
        {/* Mon Agence / Ma Team — si leader OU admin */}
        {(isAdmin || !!leaderGroupe) && (
          <SideNavItem
            icon={<IconTeam />}
            active={!selected && view === 'ma-team'}
            onClick={() => navigateTo('ma-team')}
            collapsed={!sidebarOpen && !isMobile}
            label={isAdmin ? 'Mon Agence' : 'Ma Team'}
          />
        )}
        {/* Lives Matchs — admin ou responsable habilité */}
        {(isAdmin || access?.can_manage_matchs) && (
          <SideNavItem
            icon={<IconMatchs />}
            active={!selected && view === 'lives-matchs'}
            onClick={() => navigateTo('lives-matchs')}
            collapsed={!sidebarOpen && !isMobile}
            label="Lives Matchs"
          />
        )}
        {/* Événements Live — admin ou responsable habilité */}
        {(isAdmin || access?.can_manage_events) && (
          <SideNavItem
            icon={<IconEvenements />}
            active={!selected && view === 'evenements-live'}
            onClick={() => navigateTo('evenements-live')}
            collapsed={!sidebarOpen && !isMobile}
            label="Événements Live"
          />
        )}
        {/* Lives interactifs — admin ou responsable habilité */}
        {(isAdmin || access?.can_manage_lives || access?.can_manage_themes) && (
          <SideNavItem
            icon={<IconLives />}
            active={!selected && view === 'lives'}
            onClick={() => navigateTo('lives')}
            collapsed={!sidebarOpen && !isMobile}
            label="Lives Interactifs"
          />
        )}

        <SideNavItem
          icon={<svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
          active={!selected && view === 'chat'}
          onClick={() => navigateTo('chat')}
          collapsed={!sidebarOpen && !isMobile}
          label="NS'Live Chat"
        />
      </nav>

      {/* User footer */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, padding: (!sidebarOpen && !isMobile) ? '10px 0' : '10px 14px', borderTop: '1px solid #f1f5f9', justifyContent: (!sidebarOpen && !isMobile) ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
          <ApplyInbox inline />
          <AgentChat inline />
        </div>
      )}
      <div style={{ position: 'relative' }} ref={userMenuRef}>
        {userMenuOpen && (
          <div style={{ position: 'absolute', bottom: '100%', left: 8, right: 8, marginBottom: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 160 }}>
            <button onClick={() => navigateTo('mon-profil')}
              style={{ width: '100%', padding: '11px 14px', background: view === 'mon-profil' ? '#eff6ff' : 'transparent', border: 'none', color: view === 'mon-profil' ? '#2563eb' : '#374151', fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
              <IconPerson size={14} /> Mon profil
            </button>
            {isAdmin && (
              <button onClick={() => navigateTo('settings')}
                style={{ width: '100%', padding: '11px 14px', background: view === 'settings' ? '#eff6ff' : 'transparent', border: 'none', borderTop: '1px solid #f1f5f9', color: view === 'settings' ? '#2563eb' : '#374151', fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
                <IconSettings size={14} /> Paramètres
              </button>
            )}
            <button onClick={logout}
              style={{ width: '100%', padding: '11px 14px', background: 'transparent', border: 'none', borderTop: '1px solid #f1f5f9', color: '#ef4444', fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
              <IconLogout size={14} /> Déconnexion
            </button>
          </div>
        )}
        <div onClick={() => setUserMenuOpen(o => !o)}
          style={{ padding: (!sidebarOpen && !isMobile) ? '12px 8px' : '12px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', justifyContent: (!sidebarOpen && !isMobile) ? 'center' : 'flex-start' }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}>
          {(() => {
            const sidebarAvatar = !sidebarAvatarError ? (myStreamer?.avatar_url ?? avatarUrl) : avatarUrl;
            return (
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: isAdmin ? '#ede9fe' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isAdmin ? '#7c3aed' : '#16a34a', flexShrink: 0, overflow: 'hidden' }}>
                {sidebarAvatar
                  ? <img src={sidebarAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setSidebarAvatarError(true)} />
                  : user.sub.charAt(0).toUpperCase()}
              </div>
            );
          })()}
          {(sidebarOpen || isMobile) && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.sub}</div>
                <div style={{ color: '#94a3b8', fontSize: 11 }}>{isAdmin ? 'Administrateur' : 'Streamer'}</div>
              </div>
              <span style={{ color: '#cbd5e1', fontSize: 10, flexShrink: 0, transform: userMenuOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▲</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  /* ── Rendu principal ─────────────────────────────────── */

  const renderMain = () => {
    // Streamer sélectionné dans Mon Agence → ouvre StreamerDetail
    if (selected) {
      return (
        <StreamerDetail
          streamer={selected}
          onBack={() => setSelected(null)}
          onStreamerUpdated={handleStreamerUpdated}
          onStreamerDeleted={() => { setSelected(null); handleStreamerUpdated(); }}
          isAdmin={isAdmin}
          isMobile={isMobile}
        />
      );
    }

    switch (view) {
      case 'dashboard':
        return <Dashboard isAdmin={isAdmin} isMobile={isMobile} />;

      case 'mon-live':
        if (!myStreamer) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
              <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: isMobile ? '14px 14px 12px' : '22px 32px 18px' }}>
                {!isMobile && <h1 style={{ margin: 0, color: '#0f172a', fontSize: 22, fontWeight: 700 }}>Mon Live Interactif</h1>}
                {isMobile && <h1 style={{ margin: 0, color: '#0f172a', fontSize: 18, fontWeight: 700 }}>Mon Live Interactif</h1>}
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📺</div>
                Aucun compte streamer associé à votre profil.
              </div>
            </div>
          );
        }
        return (
          <>
            {showMixingTable && (
              <MixingTableFullscreen
                streamer={myStreamer}
                onClose={() => setShowMixingTable(false)}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
              <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: isMobile ? '14px 14px 12px' : '22px 32px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                {!isMobile && <h1 style={{ margin: 0, color: '#0f172a', fontSize: 22, fontWeight: 700, flex: 1 }}>Mon Live Interactif</h1>}
                {isMobile && <h1 style={{ margin: 0, color: '#0f172a', fontSize: 18, fontWeight: 700, flex: 1 }}>Mon Live Interactif</h1>}
                <button
                  onClick={() => setShowMixingTable(true)}
                  style={{
                    background: '#0f172a', color: '#e2e8f0', border: 'none',
                    borderRadius: 8, padding: isMobile ? '6px 10px' : '8px 16px',
                    cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                    flexShrink: 0,
                  }}
                >
                  🎛️ {!isMobile && 'Table de mixage'}
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <MobileOverlayBanner streamer={myStreamer} isMobile={isMobile} />
                <RuleEditor streamer={myStreamer} onStreamerUpdated={handleStreamerUpdated} isReadOnly={false} isMobile={isMobile} />
              </div>
            </div>
          </>
        );

      case 'ma-team':
        // Admin → tous les streamers de l'agence (StreamerList cliquable)
        if (isAdmin) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
              <AgenceTab
                isMobile={isMobile}
                onSelectStreamer={setSelected}
                refreshKey={refreshKey}
                onStreamerCreated={handleStreamerUpdated}
              />
            </div>
          );
        }
        // Leader → équipe uniquement
        if (leaderGroupe) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
              <LeaderTab groupe={leaderGroupe} isMobile={isMobile} onSelectStreamer={setSelected} />
            </div>
          );
        }
        return (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
            Aucune équipe associée à votre profil.
          </div>
        );

      case 'mon-profil':
        if (myStreamer) {
          return (
            <StreamerDetail
              streamer={myStreamer}
              onBack={() => setView('dashboard')}
              onStreamerUpdated={handleStreamerUpdated}
              onStreamerDeleted={() => { setMyStreamer(null); setView('dashboard'); }}
              isAdmin={isAdmin}
              isMobile={isMobile}
              initialTab="profil"
              visibleTabs={['profil']}
            />
          );
        }
        // Admin sans streamer → ancien écran profil
        return <Profile />;

      case 'lives-matchs':
        return <LivesMatchs isMobile={isMobile} />;

      case 'evenements-live':
        return <EvenementsLive isMobile={!!isMobile} />;

      case 'mes-evenements':
        if (myStreamer) {
          return <MesEvenements streamerId={myStreamer.id} isMobile={isMobile} />;
        }
        if (isAdmin) {
          return <EvenementsLive isMobile={!!isMobile} />;
        }
        return <Dashboard isAdmin={isAdmin} isMobile={isMobile} />;

      case 'mes-matchs':
        if (myStreamer) {
          return <MesMatchs streamerId={myStreamer.id} isMobile={isMobile} />;
        }
        return <Dashboard isAdmin={isAdmin} isMobile={isMobile} />;

      case 'ressources':
        return (
          <Ressources
            isAdmin={isAdmin}
            canSeeAgentGuides={isAdmin || !!leaderGroupe || !!access?.is_responsable}
            isMobile={isMobile}
          />
        );

      case 'lives':
        return <LivesInteractifs isAdmin={isAdmin} canManageThemes={isAdmin || !!access?.can_manage_themes} isMobile={isMobile} streamerId={myStreamer?.id} />;

      case 'chat':
        return <NSLiveChat isAdmin={isAdmin} isMobile={isMobile} />;

      case 'settings':
        return <Settings isMobile={isMobile} onLogoUpdated={() => {
          setAgencyLogoError(false);
          getPublicAgency().then(r => setAgency(r.data)).catch(() => {});
        }} />;

      default:
        return <Dashboard isAdmin={isAdmin} isMobile={isMobile} />;
    }
  };

  return (
    <div style={{ display: 'flex', height: '100dvh', minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* Pop-up d'installation PWA (Axo animé) */}
      <InstallPrompt />

      {/* ── Desktop sidebar ────────────────────────── */}
      {!isMobile && <SidebarContent />}

      {/* ── Mobile: sidebar overlay ────────────────── */}
      {isMobile && sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', paddingTop: 'env(safe-area-inset-top, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', boxSizing: 'border-box' }}>
          <div onClick={() => setSidebarOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
          <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* ── Zone principale ─────────────────────────── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '8px 14px', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)', paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 14px)', paddingRight: 'calc(env(safe-area-inset-right, 0px) + 14px)', display: 'flex', alignItems: 'center', gap: 10, minHeight: 60 }}>
            <HamburgerBtn onClick={() => setSidebarOpen(true)} />
            <div style={{ width: 26, height: 26, borderRadius: 7, background: agency.logo_url && !agencyLogoError ? '#f8fafc' : 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: agency.logo_url && !agencyLogoError ? '1px solid #e2e8f0' : 'none' }}>
              {agency.logo_url && !agencyLogoError
                ? <img src={agency.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 2 }} />
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
            </div>
            <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 14 }}>{agency.name || 'NS Live'}</span>
          </div>
        )}

        {renderMain()}
      </div>


    </div>
  );
}

// ── Sidebar nav item ──────────────────────────────────────

function SideNavItem({ icon, active, onClick, collapsed, label }: { icon: React.ReactNode; active: boolean; onClick: () => void; collapsed: boolean; label: string }) {
  return (
    <button onClick={onClick} title={collapsed ? label : undefined} style={{ width: '100%', padding: collapsed ? '9px 0' : '9px 12px', borderRadius: 8, border: 'none', background: active ? '#eff6ff' : 'transparent', color: active ? '#2563eb' : '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 9, transition: 'all 0.12s' }}
      onMouseEnter={e => { if (!active) (e.currentTarget).style.background = '#f8fafc'; }}
      onMouseLeave={e => { if (!active) (e.currentTarget).style.background = 'transparent'; }}>
      <span style={{ display: 'flex', alignItems: 'center', opacity: active ? 1 : 0.65, flexShrink: 0 }}>{icon}</span>
      {!collapsed && label}
    </button>
  );
}

// ── Icons ─────────────────────────────────────────────────

function IconLives() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
}

function IconDashboard() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
}

function IconLiveInteractif() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
}

function IconTeam() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
}

function IconMatchs() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>;
}

function IconEvenements() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="2" x2="16" y2="6" strokeLinecap="round"/><line x1="8" y1="2" x2="8" y2="6" strokeLinecap="round"/><line x1="3" y1="10" x2="21" y2="10" strokeLinecap="round"/></svg>;
}

function IconRessources() {
  return <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>;
}

export function IconPerson({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
}

export function IconSettings({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
}

function IconLogout({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;
}

export default function App() {
  if (overlayMatch) {
    return <MobileOverlay streamerId={parseInt(overlayMatch[1])} />;
  }
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
